import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, FormField, Textarea, cn } from '@insolech/ui';
import { PhoneInput, validatePhone } from './PhoneInput';

/**
 * PatientForm - used in both the wizard (Create new patient) and the wizard's
 * Existing patient mode (pre-filled, editable).
 *
 * R28 layout per Rafael:
 *   Row 1: Full name (req) · Age (req, numbers) · Gender (req, Male/Female)
 *   Row 2: Weight kg (req) · Left foot size + unit (req) · Right foot size + unit (req)
 *   Row 3: Phone (opt) · Email (opt)
 *
 * Storage details:
 *   - Patient row receives first_name + last_name split from full_name (last
 *     whitespace token = last name, the rest = first name).
 *   - patients.date_of_birth is synthesized from `age` (Jan 1 of currentYear -
 *     age) at submit so the existing DB column keeps a value.
 *   - weight_kg + left/right foot size are per-prescription and live on the
 *     order line's configuration JSONB (mirrors the legacy app's
 *     scan_submissions schema where these fields lived on the order, not
 *     the patient).
 */

export type FootSizeUnit = 'EU' | 'UK' | 'US';

// Moved to a plain module so the prescription Excel exporter can reuse it
// without importing this React form. Imported locally for use below and
// re-exported for back-compat with existing consumers.
import {
  ORTHOTIC_INTENDED_USE_OPTIONS,
  type OrthoticIntendedUse,
} from './prescription/orthoticIntendedUse';
export { ORTHOTIC_INTENDED_USE_OPTIONS, type OrthoticIntendedUse };

export interface PatientFormState {
  full_name: string;
  /** Numeric age, kept as string for input handling. */
  age: string;
  /** Numeric weight (kg), kept as string for input handling. */
  weight_kg: string;
  gender: 'male' | 'female' | 'other' | 'prefer_not_to_say' | '';
  /** Numeric left foot size, kept as string. */
  left_foot_size: string;
  left_foot_unit: FootSizeUnit;
  /** Numeric right foot size, kept as string. */
  right_foot_size: string;
  right_foot_unit: FootSizeUnit;
  /** Per-order context: which use the orthotic is intended for. Stored on
   *  the line.configuration JSONB at submit time, like weight + foot size.
   *  '' until the podiatrist chooses: the form no longer preselects a value,
   *  and validatePatientForm requires one for an insole (not for a sandal,
   *  where the field is hidden). */
  orthotic_intended_use: OrthoticIntendedUse | '';
  /** Free text - patient's usual shoe brand for the chosen use (optional). */
  shoe_brand: string;
  /** Per-order: number of pairs to produce (order_lines.quantity). Kept as
   *  string for input handling, like age/weight. */
  units: string;
  /** Full E.164 phone (e.g. "+971501234567") or empty string. */
  phone: string;
  email: string;
  /** Free-text observations about the patient (optional). */
  observations: string;
}

export const EMPTY_PATIENT_FORM: PatientFormState = {
  full_name: '',
  age: '',
  weight_kg: '',
  gender: '',
  left_foot_size: '',
  left_foot_unit: 'EU',
  right_foot_size: '',
  right_foot_unit: 'EU',
  orthotic_intended_use: '',
  shoe_brand: '',
  units: '1',
  phone: '',
  email: '',
  observations: '',
};

/** Bounds for the Units (pairs) field — mirrored by the validation below and
 *  the wizard's quantity marshalling (unitsToQuantity in NewOrderPage). */
export const UNITS_MIN = 1;
export const UNITS_MAX = 10;

interface Props {
  value: PatientFormState;
  onChange: (next: PatientFormState) => void;
  /** Show validation errors. Set true after first submit attempt. */
  showErrors?: boolean | undefined;
  disabled?: boolean | undefined;
  /**
   * The order is an orthopaedic sandal. Orthotic intended use and shoe brand
   * describe the shoe an INSOLE goes into, so the sandal flow neither asks
   * for them nor requires the intended use (and the payload builders do not
   * write them, see orthoticFieldsForLine.ts).
   */
  isSandal?: boolean | undefined;
}

export interface PatientFormErrors {
  full_name?: string;
  age?: string;
  weight_kg?: string;
  gender?: string;
  left_foot_size?: string;
  right_foot_size?: string;
  orthotic_intended_use?: string;
  units?: string;
  phone?: string;
  email?: string;
}

const FOOT_SIZE_UNITS: ReadonlyArray<FootSizeUnit> = ['EU', 'UK', 'US'];

/** Returns null if no errors, otherwise an object of field → error string.
 *  `isSandal` mirrors the PatientForm prop: the sandal flow does not render
 *  the intended-use select, so it must not be required there. */
export function validatePatientForm(
  state: PatientFormState,
  opts?: { isSandal?: boolean | undefined },
): PatientFormErrors | null {
  const errors: PatientFormErrors = {};
  const fullName = (state.full_name ?? '').trim();
  if (!fullName) {
    errors.full_name = 'Patient name is required';
  } else if (fullName.split(/\s+/).length < 2) {
    errors.full_name = 'Add a surname';
  }
  const age = (state.age ?? '').trim();
  if (!age) {
    errors.age = 'Age is required';
  } else {
    const n = Number(age);
    if (!Number.isInteger(n) || n < 0 || n > 150) {
      errors.age = 'Age must be a whole number between 0 and 150';
    }
  }
  const weightKg = (state.weight_kg ?? '').trim();
  if (!weightKg) {
    errors.weight_kg = 'Weight is required';
  } else {
    const n = Number(weightKg);
    if (!Number.isFinite(n) || n <= 0 || n > 500) {
      errors.weight_kg = 'Weight must be a positive number (kg)';
    }
  }
  if (state.gender !== 'male' && state.gender !== 'female') {
    errors.gender = 'Gender is required';
  }
  // Foot size accepts free text (a shoe size, a range like "37-38", or a note
  // like "see scan") to match the clinic workflow. Required, but not numeric.
  if (!(state.left_foot_size ?? '').trim()) {
    errors.left_foot_size = 'Left foot size is required';
  }
  if (!(state.right_foot_size ?? '').trim()) {
    errors.right_foot_size = 'Right foot size is required';
  }
  if (!opts?.isSandal && !state.orthotic_intended_use) {
    errors.orthotic_intended_use = 'Intended use is required';
  }
  // `?? '1'` matches the input's display fallback: wizard state restored from
  // localStorage that predates the Units field has no `units` key, and the
  // field renders "1" for it, so validation must accept it as 1 too. An
  // explicitly cleared input ('') still errors.
  const units = (state.units ?? '1').trim();
  if (!units) {
    errors.units = 'Units is required';
  } else {
    const n = Number(units);
    if (!Number.isInteger(n) || n < UNITS_MIN || n > UNITS_MAX) {
      errors.units = `Units must be a whole number between ${UNITS_MIN} and ${UNITS_MAX}`;
    }
  }
  const phoneErr = validatePhone(state.phone);
  if (phoneErr) errors.phone = phoneErr;
  if (state.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
    errors.email = 'Email format is not valid (e.g. you@gmail.com, you@hotmail.com)';
  }
  return Object.keys(errors).length ? errors : null;
}

/**
 * Derive a `date_of_birth` for the DB when only `age` was provided.
 * Returns Jan 1 of (currentYear - age) so the synthesized DOB sorts /
 * displays correctly without claiming a specific birthday.
 */
export function deriveDobFromAge(age: string): string | null {
  // Number('') is 0, which passes the integer/range check below and would
  // yield a born-THIS-YEAR date of birth. A blank age means "unknown", so it
  // must produce null and be omitted from the write, not fabricate a DOB.
  if (!age || !age.trim()) return null;
  const n = Number(age);
  if (!Number.isInteger(n) || n < 0 || n > 150) return null;
  const year = new Date().getFullYear() - n;
  return `${year}-01-01`;
}

/** Split a full name into { first_name, last_name } for the patients table. */
export function splitFullName(full: string): { first_name: string; last_name: string } {
  const trimmed = (full ?? '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return { first_name: '', last_name: '' };
  const parts = trimmed.split(' ');
  if (parts.length === 1) return { first_name: parts[0]!, last_name: '' };
  const last = parts[parts.length - 1]!;
  const first = parts.slice(0, -1).join(' ');
  return { first_name: first, last_name: last };
}

/** Combine first_name + last_name into a single trimmed full name string. */
export function combineFullName(first: string | null | undefined, last: string | null | undefined): string {
  return [first ?? '', last ?? ''].map((s) => s.trim()).filter(Boolean).join(' ');
}

export function PatientForm({ value, onChange, showErrors, disabled, isSandal = false }: Props) {
  const { t } = useTranslation('orders');
  const errors = React.useMemo(
    () => validatePatientForm(value, { isSandal }) ?? {},
    [value, isSandal],
  );
  const update = (patch: Partial<PatientFormState>) => onChange({ ...value, ...patch });
  // R34 - per-field blur tracking so the user sees the surname error the
  // moment they tab out, not only after clicking Continue. The wizard's
  // showErrors flag is still honored as a second gate (post-Continue),
  // so this is purely additive UX.
  const [blurred, setBlurred] = React.useState<{ full_name?: boolean }>({});
  const markBlurred = (k: 'full_name') => () => setBlurred((b) => ({ ...b, [k]: true }));
  const showFullNameError = showErrors || blurred.full_name;

  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-x-3 gap-y-2.5">
      {/* Row 1 - Full name (2) · Age (2) · Gender (2), equal columns. */}
      <FormField
        label={t('form_label_full_name', { defaultValue: 'Patient name' })}
        required
        error={showFullNameError ? errors.full_name : undefined}
        className="md:col-span-2"
      >
        <Input
          value={value.full_name ?? ''}
          onChange={(e) => update({ full_name: e.target.value })}
          onBlur={markBlurred('full_name')}
          required
          disabled={disabled}
          autoFocus
          autoComplete="name"
          placeholder="First and last name"
          data-testid="patient-full-name"
        />
      </FormField>

      <FormField
        label={t('form_label_age')}
        required
        error={showErrors ? errors.age : undefined}
        className="md:col-span-2"
      >
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          max={150}
          step={1}
          value={value.age ?? ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^0-9]/g, '');
            update({ age: digits });
          }}
          required
          disabled={disabled}
          data-testid="patient-age"
          placeholder="years"
        />
      </FormField>

      <FormField
        label={t('form_label_gender')}
        required
        error={showErrors ? errors.gender : undefined}
        className="md:col-span-2"
      >
        <select
          value={value.gender}
          onChange={(e) =>
            update({ gender: e.target.value as PatientFormState['gender'] })
          }
          required
          disabled={disabled}
          data-testid="patient-gender"
          className="w-full rounded-md border border-border-default bg-surface-elevated px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">Select…</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      </FormField>

      {/* Row 2 - Weight (2) · Left foot size + unit (2) · Right foot size + unit (2) */}
      <FormField
        label={t('form_label_weight_kg')}
        required
        error={showErrors ? errors.weight_kg : undefined}
        className="md:col-span-2"
      >
        <Input
          type="number"
          inputMode="decimal"
          min={1}
          max={500}
          step={0.1}
          value={value.weight_kg ?? ''}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^0-9.]/g, '');
            const parts = cleaned.split('.');
            const next = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
            update({ weight_kg: next });
          }}
          required
          disabled={disabled}
          data-testid="patient-weight-kg"
          placeholder="kg"
        />
      </FormField>

      <FormField
        label={t('form_label_left_foot_size', { defaultValue: 'Left foot size' })}
        required
        error={showErrors ? errors.left_foot_size : undefined}
        className="md:col-span-2"
      >
        <FootSizeInput
          value={value.left_foot_size ?? ''}
          unit={value.left_foot_unit ?? 'EU'}
          onValueChange={(v) => update({ left_foot_size: v })}
          onUnitChange={(u) => update({ left_foot_unit: u })}
          disabled={disabled}
          testId="patient-left-foot-size"
        />
      </FormField>

      <FormField
        label={t('form_label_right_foot_size', { defaultValue: 'Right foot size' })}
        required
        error={showErrors ? errors.right_foot_size : undefined}
        className="md:col-span-2"
      >
        <FootSizeInput
          value={value.right_foot_size ?? ''}
          unit={value.right_foot_unit ?? 'EU'}
          onValueChange={(v) => update({ right_foot_size: v })}
          onUnitChange={(u) => update({ right_foot_unit: u })}
          disabled={disabled}
          testId="patient-right-foot-size"
        />
      </FormField>

      {/* Row 2b - Units (2) · Orthotic intended use (2) · Shoe brand (2).
          Units leads the row per Rafael (2026-07-18): pairs count is the
          field the operator must not skim past. */}
      <FormField
        label={t('form_label_units', { defaultValue: 'Units (pairs)' })}
        required
        error={showErrors ? errors.units : undefined}
        className="md:col-span-2"
      >
        <Input
          type="number"
          inputMode="numeric"
          min={UNITS_MIN}
          max={UNITS_MAX}
          step={1}
          value={value.units ?? '1'}
          onChange={(e) => {
            // Clamp at entry so wizard state can never hold an out-of-range
            // value: drafts are saved WITHOUT the form gate (onSave skips
            // validatePatientForm), so whatever sits in state is what
            // unitsToQuantity persists. Empty is allowed transiently while
            // typing; validation rejects it on Continue.
            const digits = e.target.value.replace(/[^0-9]/g, '');
            if (digits === '') {
              update({ units: '' });
              return;
            }
            const clamped = Math.min(Math.max(parseInt(digits, 10), UNITS_MIN), UNITS_MAX);
            update({ units: String(clamped) });
          }}
          required
          disabled={disabled}
          data-testid="patient-units"
          placeholder="pairs"
        />
      </FormField>

      {/* Both describe the shoe an INSOLE goes into: not asked on a sandal. */}
      {!isSandal ? (
        <>
          <FormField
            label="Orthotic intended use"
            required
            error={showErrors ? errors.orthotic_intended_use : undefined}
            className="md:col-span-2"
          >
            <select
              value={value.orthotic_intended_use}
              onChange={(e) =>
                update({
                  orthotic_intended_use: e.target
                    .value as PatientFormState['orthotic_intended_use'],
                })
              }
              required
              disabled={disabled}
              data-testid="patient-orthotic-intended-use"
              className="w-full rounded-md border border-border-default bg-surface-elevated px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {/* No preselected use: the podiatrist must choose one. The
                  placeholder is disabled so it cannot be picked back once a
                  real value is set; a stored value (reuse, reorder, resume)
                  still prefills through the state above. */}
              <option value="" disabled>
                Choose the intended use
              </option>
              {ORTHOTIC_INTENDED_USE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Shoe brand" className="md:col-span-2">
            <Input
              value={value.shoe_brand ?? ''}
              onChange={(e) => update({ shoe_brand: e.target.value })}
              disabled={disabled}
              placeholder="e.g. Nike, Asics, Brooks…"
              data-testid="patient-shoe-brand"
            />
          </FormField>
        </>
      ) : null}

      {/* Row 3 - Phone (3) · Email (3) */}
      <FormField
        label={t('form_label_phone')}
        error={showErrors ? errors.phone : undefined}
        className="md:col-span-3"
      >
        <PhoneInput
          value={value.phone}
          onChange={(v) => update({ phone: v })}
          disabled={disabled}
          error={showErrors ? errors.phone : undefined}
        />
      </FormField>

      {/*
        Email field - Phase 4b iteration round 2 fix A:
        Inline format error surfaces AS THE USER TYPES (not gated behind
        first-Continue click) when email is non-empty AND fails validation.
      */}
      <FormField
        label={t('form_label_email')}
        error={
          showErrors || (value.email && errors.email) ? errors.email : undefined
        }
        className="md:col-span-3"
      >
        <Input
          type="email"
          value={value.email}
          onChange={(e) => update({ email: e.target.value })}
          disabled={disabled}
          autoComplete="email"
          data-testid="patient-email"
          placeholder="patient@gmail.com"
        />
      </FormField>

      {/* Row 4 - Patient notes (optional, full width, single-row default). */}
      <FormField
        label={t('form_label_patient_notes', { defaultValue: 'Patient notes' })}
        className="md:col-span-6"
      >
        <Textarea
          rows={2}
          value={value.observations ?? ''}
          onChange={(e) => update({ observations: e.target.value })}
          disabled={disabled}
          className="text-sm"
          data-testid="patient-observations"
        />
      </FormField>
    </div>
  );
}

// ---- Foot size input with attached unit dropdown ------------------------

function FootSizeInput({
  value,
  unit,
  onValueChange,
  onUnitChange,
  disabled,
  testId,
}: {
  value: string;
  unit: FootSizeUnit;
  onValueChange: (v: string) => void;
  onUnitChange: (u: FootSizeUnit) => void;
  disabled?: boolean | undefined;
  testId?: string | undefined;
}) {
  return (
    <div
      className={cn(
        'flex items-stretch w-full rounded-md border border-border-default bg-surface-elevated',
        'focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500',
        disabled ? 'opacity-60 cursor-not-allowed' : '',
      )}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={disabled}
        placeholder="Size or note (e.g. 42, 37-38, see scan)"
        data-testid={testId}
        className="flex-1 min-w-0 bg-transparent px-3 py-2 text-sm rounded-l-md focus:outline-none"
      />
      <div className="border-l border-border-default" aria-hidden="true" />
      <select
        value={unit}
        onChange={(e) => onUnitChange(e.target.value as FootSizeUnit)}
        disabled={disabled}
        aria-label="Foot size unit"
        className="bg-transparent px-2 py-2 text-xs font-medium text-text-secondary rounded-r-md focus:outline-none"
      >
        {FOOT_SIZE_UNITS.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
    </div>
  );
}
